'use client'
import { forwardRef, useEffect, useRef, useState, useImperativeHandle } from 'react'

import { cn, getLocalizedPath } from '../../helpers/utils.js'
import Picture from '../../components/picture.js'
import { withLayout } from '../../shared/Styles.js'
import type { BrandCardLinkProps, BrandCardLinkItemProps } from './types.js'

import type { Img } from '../../types/props.js'
import { useMediaQuery } from 'react-responsive'
import { useExposure } from '../../hooks/useExposure.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, FreeMode, Mousewheel } from 'swiper/modules'
import Title from '../Title/index.js'
import Link from '../../components/link.js'

const componentType = 'copy'
const componentName = 'store_benefits'

const chunkArray = (arr: BrandCardLinkItemProps[], size: number) => {
  const chunks = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

const BrandCardLink = forwardRef<HTMLDivElement, BrandCardLinkProps>(
  ({ data: { items = [], itemShape }, className }, ref) => {
    const [isMobile, setIsMobile] = useState(false)
    const mediaQuery = useMediaQuery({ query: '(max-width: 768px)' })
    const paginationRef = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)
    const { locale = 'us' } = useAiuiContext()

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)
    useExposure(innerRef, {
      componentType,
      componentName,
    })

    useEffect(() => {
      setIsMobile(mediaQuery)
    }, [mediaQuery])

    const itemsArray = isMobile ? chunkArray(items, 3) : items
    return (
      <div ref={innerRef} className={cn('brand-equity-wrapper w-full', className)}>
        {isMobile ? (
          <Swiper
            className={cn(className)}
            modules={[FreeMode, Mousewheel, Pagination]}
            freeMode={true}
            mousewheel={{
              forceToAxis: true,
            }}
            pagination={{
              clickable: true,
              el: paginationRef.current,
            }}
            breakpoints={{
              0: {
                spaceBetween: 12,
              },
            }}
          >
            {itemsArray?.map((item, jIndex) => (
              <SwiperSlide key={'SwiperSlide' + jIndex} className="!flex flex-col gap-3">
                {Array.isArray(item) &&
                  item?.map((item, index) => (
                    <BrandCardLinkItem key={index} data={item} itemShape={itemShape} index={index} locale={locale} />
                  ))}
              </SwiperSlide>
            ))}
            <div ref={paginationRef} className="mt-3 flex justify-center"></div>
          </Swiper>
        ) : (
          <div className={cn('laptop:gap-4 tablet:grid-cols-2 laptop:grid-cols-4 grid grid-cols-1 gap-3')}>
            {items.map((item, index) => (
              <BrandCardLinkItem key={index} data={item} itemShape={itemShape} index={index} locale={locale} />
            ))}
          </div>
        )}
      </div>
    )
  }
)

const BrandCardLinkItem = ({
  data,
  itemShape,
  index,
  locale,
}: {
  data: BrandCardLinkItemProps
  itemShape?: 'round' | 'square'
  index: number
  locale: string
}) => {
  return (
    <div
      className={cn(
        'brand-equity-item',
        'lg-desktop:p-8 desktop:p-6 p-4',
        // 'laptop:h-[192px] lg-desktop:h-[240px] h-[160px]',
        'box-border flex w-full flex-col justify-between overflow-hidden bg-[#DAE7F2]',
        itemShape === 'round' ? 'rounded-box' : 'rounded-none'
      )}
    >
      <div className="lg-desktop:h-[50px] flex h-10 items-center justify-between gap-3">
        <h3 className="link-item-title lg-desktop:text-[18px] desktop:text-[16px] line-clamp-2 flex-1 text-[14px] font-bold leading-[1.2] tracking-[-0.02em] text-[#080A0F]">
          {data.title}
        </h3>
        <div className="link-item-img lg-desktop:size-[36px] size-[30px] shrink-0">
          <Picture
            className="size-full object-cover"
            source={data.icon?.url}
            alt={data.icon?.alt}
            imgClassName="w-full h-full object-cover"
          />
        </div>
      </div>
      <div className="box-border overflow-hidden">
        <p
          title={data.description || ''}
          className="link-item-description lg-desktop:text-[24px] line-clamp-4 text-[20px] font-semibold leading-[1.2] tracking-[-0.04em] text-[#767880]"
        >
          {data.description || ''}
        </p>
        <Link
          href={getLocalizedPath(data?.link || '', locale)}
          asChild={!data?.link}
          className="desktop:mt-4 mt-2 flex items-center gap-0.5 no-underline hover:underline"
        >
          <span className="link-item-text laptop:text-base text-sm">{data?.linkText}</span>
          <div className="size-6">
            <svg
              width="24"
              height="24"
              className="mb-[3px]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                className="transition-all duration-[0.4s]"
                d="M10.5 8L14.5 12L10.5 16"
                stroke="#777"
                strokeWidth="1.5"
                strokeLinecap="round"
              ></path>
            </svg>
          </div>
        </Link>
      </div>
    </div>
  )
}

BrandCardLink.displayName = 'BrandCardLink'

export default withLayout(BrandCardLink)
